/**
 * Regra pura do Plano de Ação (metodologia FACR). Decide QUAIS itens da
 * avaliação viram item-ação: os de maturidade inicial (`mi`) baixa (0–2). Itens
 * CORE (peso 3 — mesma definição do IMGQ, ver `imgq.ts`) ficam obrigatórios;
 * os demais, sugeridos.
 *
 * Núcleo funcional puro (doc 13.1): mesma entrada → mesma saída, sem IO. Usada
 * tanto no backend (reconciliação no sync) quanto no PWA (prévia ao vivo).
 */
import type { NivelMaturidade } from "./types.ts";

/** Maturidades iniciais que disparam a criação de um item-ação. */
const MATURIDADES_GATILHO: ReadonlySet<string> = new Set(["0", "1", "2"]);

/** Peso que caracteriza um item CORE (essencial). */
const PESO_CORE = 3;

/** Uma resposta reduzida ao que a regra precisa: item + maturidade inicial. */
export interface RespostaMaturidade {
  readonly itemId: string;
  readonly mi: NivelMaturidade | null | undefined;
}

/** Um item-ação gerado a partir de um achado de maturidade baixa. */
export interface ItemPlanoGerado {
  readonly itemId: string;
  readonly peso: number;
  readonly obrigatorio: boolean; // true = CORE (peso 3)
  readonly maturidadeGatilho: string; // "0" | "1" | "2"
}

/** CORE = peso 3 (mesma regra do IMGQ, `imgq.ehCore`). */
export function ehCorePeso(peso: number): boolean {
  return peso === PESO_CORE;
}

/** Um item entra no plano quando a maturidade inicial (`mi`) está em 0–2. */
export function maturidadeDisparaPlano(mi: NivelMaturidade | null | undefined): boolean {
  return mi != null && MATURIDADES_GATILHO.has(mi);
}

/**
 * Seleciona os itens-ação: `mi` ∈ {0,1,2} e presentes no catálogo ativo (`pesos`).
 * CORE (peso 3) fica obrigatório; os demais, sugeridos. Ordena CORE primeiro,
 * depois por peso desc e `itemId` — determinístico (mesma entrada → mesma ordem).
 */
export function itensParaPlano(
  respostas: readonly RespostaMaturidade[],
  pesos: ReadonlyMap<string, number>,
): ItemPlanoGerado[] {
  const gerados: ItemPlanoGerado[] = [];
  for (const r of respostas) {
    if (!maturidadeDisparaPlano(r.mi)) {
      continue;
    }
    const peso = pesos.get(r.itemId);
    if (peso === undefined) {
      continue; // item fora do catálogo ativo (ex.: recodificado) — ignora
    }
    gerados.push({
      itemId: r.itemId,
      peso,
      obrigatorio: ehCorePeso(peso),
      maturidadeGatilho: r.mi as string,
    });
  }
  return gerados.sort((a, b) => {
    if (a.obrigatorio !== b.obrigatorio) {
      return a.obrigatorio ? -1 : 1; // obrigatórios (CORE) primeiro
    }
    if (a.peso !== b.peso) {
      return b.peso - a.peso; // maior peso primeiro
    }
    return a.itemId.localeCompare(b.itemId);
  });
}

/**
 * Recorte de itens-ação pelo escopo de setores de uma conta `unidade`
 * (metodologia FACR — Portal da Unidade). `setores === null` (diretor, ou conta
 * sem escopo) devolve tudo, sem filtro; caso contrário mantém só os itens cujo
 * setor — resolvido por `setorPorItem` (itemId → código do setor, do catálogo) —
 * está no conjunto permitido. Puro e genérico sobre `{ itemId }`: serve tanto à
 * autorização no backend quanto ao agrupamento por setor na UI.
 */
export function itensDoEscopo<T extends { readonly itemId: string }>(
  itens: readonly T[],
  setorPorItem: ReadonlyMap<string, string>,
  setores: readonly string[] | null,
): T[] {
  if (setores === null) {
    return [...itens];
  }
  const permitidos = new Set(setores);
  return itens.filter((i) => {
    const setor = setorPorItem.get(i.itemId);
    return setor !== undefined && permitidos.has(setor);
  });
}

/**
 * Um item-ação está no escopo? `setores === null` (diretor) ⇒ sempre; senão o
 * setor do item (via `setorPorItem`) precisa existir e estar na lista. Item fora
 * do catálogo (setor desconhecido) é negado para contas com escopo — base da
 * checagem de escrita (responder item) no Portal da Unidade.
 */
export function itemNoEscopo(
  itemId: string,
  setorPorItem: ReadonlyMap<string, string>,
  setores: readonly string[] | null,
): boolean {
  if (setores === null) {
    return true;
  }
  const setor = setorPorItem.get(itemId);
  return setor !== undefined && setores.includes(setor);
}
